import React from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical } from 'lucide-react';

/**
 * Shipping-experiment notice: what BOTH arms of a running A/B shipping
 * experiment pay for this cart. Rendered from the backend's read-only block
 * (shipping_experiments.both_arms): Checkout Preview (`experiment_arms`) and
 * the simulator (`shipping_experiment`, which may also carry the real order's
 * `conversion`). The page computes nothing.
 */

// Same arm colours as the Experiments page (validated light + dark).
const ARM_DOT = {
  control: 'bg-[#2a78d6] dark:bg-[#3987e5]',
  treatment: 'bg-[#eb6834] dark:bg-[#d95926]',
};
const ARM_LABEL = { control: 'control', treatment: 'test group' };

const eur = (v) => (v === null || v === undefined ? '–' : `€${Number(v).toFixed(2)}`);
const eur0 = (v) => (v === null || v === undefined ? '–' : `€${Number(v) % 1 ? Number(v).toFixed(2) : Number(v).toFixed(0)}`);
const isFrancoRule = (x) => x?.rule_type === 'franco_threshold';
const pct = (v, d = 0) => (v === null || v === undefined ? '–' : `${(v * 100).toFixed(d)}%`);

function ArmDot({ arm }) {
  return <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${ARM_DOT[arm] || 'bg-muted-foreground'}`} aria-hidden="true" />;
}

/** The price a customer sees: incl. VAT when the country charges it. */
function Price({ ex, incl, vat }) {
  if (ex === null || ex === undefined) return <b>–</b>;
  if (vat > 0 && incl !== null && incl !== undefined) {
    return <><b className="tabular-nums">{eur(incl)}</b> <span className="text-[10px] opacity-75">({eur(ex)} ex VAT)</span></>;
  }
  return <b className="tabular-nums">{eur(ex)}</b>;
}

export default function ExperimentNotice({ block, mode = 'simulator', compact = false }) {
  if (!block) return null;
  const conv = block.conversion;
  const running = block.running !== false;
  const hasPrices = block.control_price_eur !== null && block.control_price_eur !== undefined;
  const vat = block.vat_rate || 0;
  // Franco-threshold experiment: the test group gets FREE shipping between the
  // lowered threshold and the normal one (same Free Shipping rate set as any
  // franco cart); nothing is "discounted".
  const franco = isFrancoRule(block);
  const thresholdText = franco ? `threshold ${eur0(block.threshold_to)} instead of ${eur0(block.threshold_from)}` : '';

  return (
    <div className="rounded-lg border border-indigo-300 bg-indigo-50 p-3 text-xs leading-relaxed text-indigo-950 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-100">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 font-semibold">
          <FlaskConical className="h-3.5 w-3.5 shrink-0" />
          <span>
            {running ? 'Shipping experiment running' : 'Shipping experiment'}{block.country ? ` in ${block.country}` : ''}: {block.name}
          </span>
        </div>
        {block.link && (
          <Link to={block.link} className="text-[11px] font-medium underline underline-offset-2 hover:no-underline">
            View experiment
          </Link>
        )}
      </div>

      {running && hasPrices && (
        block.eligible ? (
          <div className={`mt-2 grid gap-2 ${compact ? '' : 'sm:grid-cols-2'}`}>
            <div className="rounded-md border border-indigo-200 bg-white/60 px-2.5 py-1.5 dark:border-indigo-900 dark:bg-indigo-950/40">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide opacity-80"><ArmDot arm="control" /> control ({franco ? `normal threshold ${eur0(block.threshold_from)}` : 'normal price'})</div>
              <div className="mt-0.5 text-sm"><Price ex={block.control_price_eur} incl={block.control_price_incl_vat} vat={vat} /></div>
            </div>
            <div className="rounded-md border border-indigo-200 bg-white/60 px-2.5 py-1.5 dark:border-indigo-900 dark:bg-indigo-950/40">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide opacity-80"><ArmDot arm="treatment" /> test group</div>
              <div className="mt-0.5 text-sm">
                {franco && !block.budget_spent ? (
                  <><b>Free shipping</b> <span className="ml-1 text-[11px] font-medium">({thresholdText})</span></>
                ) : (
                  <>
                    <Price ex={block.treatment_price_eur} incl={block.treatment_price_incl_vat} vat={vat} />
                    {!block.budget_spent && <span className="ml-1.5 text-[11px] font-medium">(−{eur(block.discount_eur)})</span>}
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <p className="mt-1.5">
            Not eligible: <strong>{block.reason_label || block.reason}</strong>. Both groups pay the normal price
            {' '}<Price ex={block.control_price_eur} incl={block.control_price_incl_vat} vat={vat} />.
          </p>
        )
      )}
      {running && !hasPrices && (
        <p className="mt-1.5">Not eligible: <strong>{block.reason_label || block.reason}</strong>. Nothing to discount.</p>
      )}

      {running && block.eligible && block.budget_spent && (
        <p className="mt-1.5">This week&rsquo;s budget is spent: the test group pays the normal price until Monday (the {franco ? 'free shipping' : 'discount'} would be {eur(block.discount_eur)}).</p>
      )}

      {running && (
        <p className="mt-1.5 text-[11px] opacity-80">
          {mode === 'preview' ? (
            block.arm_known
              ? <span className="inline-flex items-center gap-1">This address is in the <ArmDot arm={block.arm} /> <strong>{ARM_LABEL[block.arm] || block.arm}</strong>.</span>
              : 'No street entered: arm unknown (postcode + street pick the arm, like for a customer).'
          ) : franco ? (
            `Franco threshold test: the test group gets the normal Free Shipping rate set from ${eur0(block.threshold_to)} (instead of ${eur0(block.threshold_from)}); the solution below is the normal price.`
          ) : (
            'Only the default rate (Standard / Oversized / Pallet Shipping) is discounted; the solution below is the normal price.'
          )}
          {' '}Margin floor {pct(block.floor)}
          {block.margin_pct !== null && block.margin_pct !== undefined && <>, this cart {pct(block.margin_pct, 1)} {franco ? 'if shipping were free' : 'after shipping'}</>}
          {!franco && block.share !== null && block.share !== undefined && <>, shipping {pct(block.share, 1)} of the cart (min {pct(block.min_share)})</>}.
        </p>
      )}

      {conv && (
        <p className="mt-1.5 flex flex-wrap items-center gap-1">
          <span>Order {conv.order_number} was in the</span>
          <ArmDot arm={conv.arm} />
          <strong>{ARM_LABEL[conv.arm] || conv.arm}</strong>
          <span>
            {conv.arm === 'treatment' && franco && conv.discount_eur > 0
              ? <>and got <strong>free shipping</strong> below the normal threshold (giveaway {eur(conv.discount_eur)}, paid {eur(conv.shipping_paid_eur)}).</>
              : conv.arm === 'treatment'
              ? (conv.discount_eur > 0 ? <>and got <strong>{eur(conv.discount_eur)}</strong> off shipping (paid {eur(conv.shipping_paid_eur)}).</> : <>but got no discount (paid {eur(conv.shipping_paid_eur)}).</>)
              : <>and paid the normal price ({eur(conv.shipping_paid_eur)}).</>}
          </span>
        </p>
      )}
    </div>
  );
}

/**
 * Checkout log: one quote's experiment decision (response_data.experiment, as
 * the carrier service stored it). `experiments` maps slug -> {id, name, countries,
 * link} (list API); `labels` maps reason -> text. Older quotes have no block.
 */
function quoteSummary(exp, labels) {
  const d = Number(exp.discount || 0).toFixed(2);
  if (isFrancoRule(exp)) {
    const th = `threshold ${eur0(exp.threshold_to)} instead of ${eur0(exp.threshold_from)}`;
    if (exp.applied) return { tone: 'applied', text: `test group: free shipping (${th})` };
    if (exp.reason === 'eligible' || exp.reason === 'budget') {
      if (exp.arm === 'treatment') return { tone: 'shadow', text: `test group · budget spent, normal price (€${d})` };
      return { tone: 'shadow', text: `control · normal threshold, shadow giveaway €${d}` };
    }
  }
  if (exp.applied) return { tone: 'applied', text: `test group · −€${d} applied` };
  if (exp.reason === 'eligible' || exp.reason === 'budget') {
    if (exp.arm === 'treatment') return { tone: 'shadow', text: `test group · budget spent, no discount (€${d})` };
    return { tone: 'shadow', text: `control · shadow €${d}` };
  }
  const label = (labels && labels[exp.reason]) || exp.reason || 'not evaluated';
  return { tone: 'none', text: `${exp.arm ? `${ARM_LABEL[exp.arm] || exp.arm} · ` : ''}not eligible: ${label}` };
}

const PILL_TONE = {
  applied: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
  shadow: 'bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:ring-indigo-800',
  none: 'bg-muted text-muted-foreground',
};

function expTitle(exp, experiments) {
  const meta = (experiments || {})[exp.experiment];
  if (meta?.countries?.length) return `Experiment ${meta.countries.join('+')}`;
  return `Experiment ${meta?.name || exp.experiment}`;
}

export function ExperimentQuotePill({ exp, experiments, labels }) {
  if (!exp || typeof exp !== 'object') return null;
  const s = quoteSummary(exp, labels);
  const meta = (experiments || {})[exp.experiment];
  const body = (
    <span className={`inline-flex max-w-[260px] items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${PILL_TONE[s.tone]}`}
      title={`${meta?.name || exp.experiment}: ${s.text}`}>
      <FlaskConical className="h-2.5 w-2.5 shrink-0" />
      <span className="truncate">{expTitle(exp, experiments)} · {s.text}</span>
    </span>
  );
  return meta?.link
    ? <Link to={meta.link} onClick={(e) => e.stopPropagation()} className="hover:opacity-80">{body}</Link>
    : body;
}

export function ExperimentQuoteDetail({ exp, experiments, labels }) {
  if (!exp || typeof exp !== 'object') return null;
  const meta = (experiments || {})[exp.experiment];
  const s = quoteSummary(exp, labels);
  const eligible = exp.reason === 'eligible' || exp.reason === 'budget';
  const row = (k, v) => (
    <div className="flex items-center justify-between gap-3"><span className="opacity-80">{k}</span><span className="font-mono text-right">{v}</span></div>
  );
  return (
    <div className="rounded border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-[11px] space-y-0.5 text-indigo-950 dark:border-indigo-800 dark:bg-indigo-950/30 dark:text-indigo-100">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 font-medium"><FlaskConical className="h-3 w-3" />Shipping experiment: {meta?.name || exp.experiment}</span>
        {meta?.link && <Link to={meta.link} onClick={(e) => e.stopPropagation()} className="underline underline-offset-2 hover:no-underline">View experiment</Link>}
      </div>
      {row('Arm', exp.arm ? <span className="inline-flex items-center gap-1"><ArmDot arm={exp.arm} />{ARM_LABEL[exp.arm] || exp.arm}</span> : 'none (no address yet)')}
      {row('Outcome', s.text)}
      {row('Reason', (labels && labels[exp.reason]) || exp.reason || '–')}
      {isFrancoRule(exp) && row('Franco threshold', `${eur0(exp.threshold_from)} normal, ${eur0(exp.threshold_to)} test group`)}
      {row(isFrancoRule(exp) ? 'Free shipping given' : 'Discount', `${eur(exp.discount)}${exp.applied ? ' (applied)' : eligible ? ' (shadow, not applied)' : ''}`)}
      {row('Shipping before → after (ex VAT)', `${eur(exp.shipping_before)} → ${eur(exp.shipping_after)}`)}
    </div>
  );
}
